import { omit, pickBy, isEmpty, cloneDeep } from 'lodash';
import { enrichWorkflowData } from '~/assets/javascript/utils';
import { formatWorkflow } from '~/assets/javascript/utils/workflow';
import { useNuxtApp } from '#app';

export const state = () => ({
  loadingWorkflows: false,
  loadingCurrentWorkflow: true,
  loadingWorkflowSteps: true,
  reloadingCurrentWorkflow: false,
  workflows: [],
  workflow: {},
  stepIdLoading: null,
  stepErrors: {},
  stepsVisibility: {},
  workflowStepsEntityDependencies: {},
});

const getMappingItem = (key, step) => {
  const itemFromMapping = step.mapping[key];
  if (itemFromMapping) return itemFromMapping;
  const fallbackItem = {};

  if (step.action_schema[key].default !== undefined) {
    fallbackItem.value = step.action_schema[key].default;
  }

  return fallbackItem;
};

export const getters = {
  currentWorkflow: state => state.workflow,
  currentTrigger: (_, { currentWorkflow }) => currentWorkflow.trigger || {},
  triggerHasDynamicSchema: (_, { currentTrigger }) => ['Webhook', 'WorkflowRequest'].includes(currentTrigger.trigger_type),
  triggerHasRecordAssociated: (_, { triggerHasDynamicSchema }) => !triggerHasDynamicSchema,
  currentSheet: (_, { currentTrigger }, rootState) => {
    const sheets = rootState.viewOptions.sheetsCache || [];

    if (currentTrigger.sheet_id) return sheets.find(({ id }) => id === currentTrigger.sheet_id);

    const viewIds = currentTrigger.views.map(view => view.id);

    return sheets.find(
      sheet => sheet.views.find(({ id }) => viewIds.includes(id)),
    );
  },
  currentViews: (_, { currentTrigger }) => currentTrigger.views || [],
  availableFields: (_, { currentTrigger }) => currentTrigger?.fields || [],
  workflowStepsMap: (_, { currentWorkflow }) => {
    const stepsMap = {};

    const collectSteps = (steps) => {
      steps.forEach((step) => {
        stepsMap[step.id] = step;

        if (step.sorted_steps) {
          collectSteps(step.sorted_steps);
        }
      });
    };

    if (currentWorkflow.sorted_steps) {
      collectSteps(currentWorkflow.sorted_steps);
    }

    return stepsMap;
  },
  isArchived: ({ workflow }) => Boolean(workflow.archived_at),
};

export const mutations = {
  assign(state, payload) {
    Object.assign(state, payload);
  },
  setWorkflows(state, workflows) {
    state.workflows = workflows;
  },
  setLoadingCurrentWorkflow(state, loading) {
    state.loadingCurrentWorkflow = loading;
  },
  setLoadingWorkflowSteps(state, loading) {
    state.loadingWorkflowSteps = loading;
  },
  setReloadingCurrentWorkflow(state, reloading) {
    state.reloadingCurrentWorkflow = reloading;
  },
  setCurrentWorkflowWithoutSteps(state, workflow) {
    state.workflow = workflow;
  },
  setCurrentWorkflow(state, workflow) {
    state.workflow = formatWorkflow(workflow, useNuxtApp().$i18n.locale.value);

    state.stepErrors = {};
  },
  setStepsVisibility(state, visible) {
    if (visible) {
      const visibility = Object.keys(state.workflow.stepsById).reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {});
      state.stepsVisibility = visibility;
    } else {
      state.stepsVisibility = {};
    }
  },
  setStepVisibility(state, { stepId, visible }) {
    if (visible) {
      state.stepsVisibility[stepId] = visible;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete state.stepsVisibility[stepId];
    }
  },
  setLoadingStepId(state, id) {
    state.stepIdLoading = id;
  },
  setStepErrors(state, { stepId, errors }) {
    state.stepErrors[stepId] = errors;
  },
  updateLocalStepMapping(state, { workflowId, stepId, key, value, mapping }) {
    if (workflowId !== state.workflow.id) return;

    const step = state.workflow.stepsById[stepId];

    if (mapping) {
      step.mapping = mapping;
    } else {
      step.mapping[key] = value;
    }
  },
  updateTriggerViews(state, { workflowTriggerId, views }) {
    const workflow = state.workflows.find(({ trigger }) => trigger?.id === workflowTriggerId);

    if (workflow) {
      workflow.trigger.views = views;
    }

    if (state.workflow.trigger?.id === workflowTriggerId) {
      state.workflow.trigger.views = views;
    }
  },
  setWorkflowStepsEntityDependencies(state, dependencies) {
    state.workflowStepsEntityDependencies = dependencies;
  },
};

export const actions = {
  reloadCurrentWorkflow({ commit, getters }) {
    const currentWorkflowId = getters.currentWorkflow.id;
    if (!currentWorkflowId) return null;
    commit('setReloadingCurrentWorkflow', true);
    const { $apiClient } = useNuxtApp();

    return $apiClient.workflows.get(currentWorkflowId).then((workflow) => {
      commit('setCurrentWorkflow', workflow);
      commit('setReloadingCurrentWorkflow', false);
    });
  },
  loadCurrentWorkflow({ commit }, id) {
    if (id) {
      const { $apiClient } = useNuxtApp();

      let isLoadingSteps = true;
      commit('setLoadingCurrentWorkflow', true);
      commit('setLoadingWorkflowSteps', true);

      const promises: Promise<any>[] = [];

      promises.push($apiClient.workflows.get(id).then((workflow) => {
        isLoadingSteps = false;
        commit('setCurrentWorkflow', workflow);
        commit('setLoadingCurrentWorkflow', false);
        commit('setLoadingWorkflowSteps', false);
      }));

      promises.push($apiClient.workflows.get(id, { without_steps: true }).then((workflow) => {
        // If the steps are already loaded, we don't want to update the state because it already has a more complete data
        if (!isLoadingSteps) return;

        commit('setCurrentWorkflowWithoutSteps', workflow);
        commit('setLoadingCurrentWorkflow', false);
      }));

      return Promise.all(promises).catch((error) => {
        commit('setLoadingCurrentWorkflow', false);
        throw error;
      });
    }

    return commit('setCurrentWorkflow', {});
  },
  async loadWorkflows({ commit, rootGetters, state }) {
    if (state.loadingWorkflows) return;

    commit('assign', { loadingWorkflows: true, stepIdLoading: null });
    const { $errorRescue, $apiClient } = useNuxtApp();

    try {
      const workflows = await $apiClient.workflows.list();

      const categories = rootGetters['workspace/categoryHierarchy'];
      const enrichedWorkflows = enrichWorkflowData(workflows, categories);
      commit('assign', { workflows: enrichedWorkflows });
    } catch (error) {
      $errorRescue(this, error, 'loadWorkflows');
    } finally {
      commit('assign', { loadingWorkflows: false });
    }
  },
  async updateStep({ commit, getters }, { stepId, payload }) {
    const { $api, $errorRescue, $apiClient, $asyncDataManager } = useNuxtApp();
    const workflowId = getters.currentWorkflow.id;

    const saveMethod = () => {
      commit('setLoadingStepId', stepId);
      commit('setStepErrors', { stepId, errors: [] });

      return $api.$patch(`/workflow_steps/${stepId}`, { body: payload });
    };

    const onSave = () => {
      commit('setLoadingStepId', null);
    };

    const onSaveError = (error) => {
      const errors = error.data?.errors;

      if (errors) commit('setStepErrors', { stepId, errors });

      commit('setLoadingStepId', null);

      $errorRescue(this, error, 'updateStep', { onlyReturnErrorMsg: true, silenceRollbar: true });
    };

    const loadMethod = () => {
      const currentWorkflowId = getters.currentWorkflow.id;

      if (!currentWorkflowId || workflowId !== currentWorkflowId) return Promise.resolve();

      commit('setReloadingCurrentWorkflow', true);

      return $apiClient.workflows.get(getters.currentWorkflow.id);
    };

    const onLoad = (workflow) => {
      if (!workflow || getters.currentWorkflow.id !== workflow.id) return;

      commit('setCurrentWorkflow', workflow);
      commit('setReloadingCurrentWorkflow', false);
    };

    return $asyncDataManager.save({
      loadMethod,
      onLoad,
      onSave,
      onSaveError,
      loadId: `workflow__${workflowId}`,
      saveId: `workflow-step__${stepId}`,
      saveMethod,
    });
  },
  async updateStepMapping({ state, commit, dispatch }, { stepId, payload }) {
    const step = state.workflow.stepsById[stepId];
    const mapping = payload;

    Object.keys(mapping).forEach((mappingKey) => {
      const schema = step.action_schema[mappingKey];

      if (isEmpty(mapping[mappingKey])) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete mapping[mappingKey];
      }

      if (schema.display_if) {
        const { key, accessor, value } = schema.display_if;
        const mappingItem = getMappingItem(key, step);

        if (
          (accessor === 'value' && mappingItem.value !== value)
          || (accessor === 'type' && mappingItem.type !== value)
        ) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete mapping[mappingKey];
        }
      }
    });

    commit('updateLocalStepMapping', { workflowId: step.workflow_id, stepId, mapping });

    return dispatch('updateStep', { stepId, payload: { mapping: payload } });
  },
  removeStepMapping({ state, dispatch }, { stepId, keys }) {
    const step = state.workflow.stepsById[stepId];

    const newMapping = omit(step.mapping, keys);

    return dispatch('updateStepMapping', { stepId, payload: newMapping });
  },
  incrementStepMapping({ state, dispatch }, { stepId, payload }) {
    const step = state.workflow.stepsById[stepId];

    const newMapping = pickBy(
      {
        ...step.mapping,
        ...payload,
      },
      (_, key) => key in step.action_schema,
    );

    return dispatch('updateStepMapping', {
      stepId,
      payload: newMapping,
    });
  },
  async createWorkflow({ commit }, { name, triggerType, appCategoryName }) {
    const { $apiClient } = useNuxtApp();
    const workflow = await $apiClient.workflows.create({
      name,
      trigger_type: triggerType,
      app_category_name: appCategoryName,
    });

    commit('setWorkflows', [...(state.workflows || []), workflow]);

    return workflow;
  },
  async removeWorkflow({ commit, state }, { workflowId }) {
    const { $api } = useNuxtApp();
    await $api.delete(`/workflows/${workflowId}`);

    if (state.workflow.id === workflowId) {
      commit('setCurrentWorkflow', {});
    }

    commit('assign', { workflows: [...state.workflows.filter(({ id }) => id !== workflowId)] });
  },
  async updateWorkflow({ commit, state: { workflows, workflow } }, { workflowId, ...workflowData }) {
    const { $apiClient } = useNuxtApp();
    await $apiClient.workflows.update(workflowId, workflowData);

    const workflowToUpdate = workflowId === workflow.id
      ? workflow
      : workflows.find(({ id }) => id === workflowId);
    const updatedWorkflow = { ...workflowToUpdate, ...workflowData };

    // update updated workflow in workflows array
    const updatedWorkflows = workflows.map((workflowItem) => {
      if (workflowItem.id === workflowId) {
        return updatedWorkflow;
      }

      return workflowItem;
    });

    // update current workflow if it's the same as updated
    if (workflowId === workflow.id) { commit('setCurrentWorkflow', updatedWorkflow); }

    commit('assign', { workflows: [...updatedWorkflows] });
  },
  async updateTrigger({ dispatch }, { sheetId, workflowTriggerId, paramsMap, sync, forceAsync, options, schema, outputSchema }) {
    const { $errorRescue, $apiClient, $analytics } = useNuxtApp();

    try {
      const updateTriggerTrackPayloadMap = {
        sheet: { newState: sheetId, property: 'sheet_id' },
        paramsMap: { property: 'params_map' },
        sync: { newState: sync, property: 'sync' },
        forceAsync: { newState: forceAsync, property: 'forceAsync' },
        options: { newState: options, property: 'options' },
        schema: { newState: schema, property: 'schema' },
        outputSchema: { newState: outputSchema, property: 'output_schema' },
        default: {},
      };
      const key = (sheetId && 'sheet')
        || (paramsMap && 'params_map')
        || (sync && 'sync')
        || (forceAsync && 'forceAsync')
        || (options && 'options')
        || (schema && 'schema')
        || (outputSchema && 'outputSchema')
        || 'default';

      $analytics.track('updateTrigger', updateTriggerTrackPayloadMap[key]);

      const trigger = await $apiClient.workflowTriggers.update(
        workflowTriggerId,
        {
          sheet_id: sheetId,
          params_map: paramsMap,
          sync,
          force_async: forceAsync,
          options,
          schema,
          output_schema: outputSchema,
        },
      );

      await dispatch('reloadCurrentWorkflow');
      return trigger;
    } catch (error) {
      $errorRescue(this, error, 'updateTrigger');

      return null;
    }
  },
  async removeWorkflowStep({ dispatch, commit }, stepId) {
    commit('assign', { loadingWorkflows: true });

    try {
      const { $api } = useNuxtApp();
      await $api.delete(`/workflow_steps/${stepId}`);
      await dispatch('reloadCurrentWorkflow');
    } catch (err) {
      commit('assign', { loadingWorkflows: false });
      throw err;
    }
  },
  async updateCurrentTriggerViews({ commit, dispatch, getters: { currentTrigger } }, { views }) {
    const { $apiClient } = useNuxtApp();
    const currentTriggerViews = currentTrigger.views;
    let newTriggerViews = cloneDeep(currentTriggerViews);
    // We need to delete all workflow_trigger_views that are not in the views array but are in the currentTriggerViews array
    // and create all workflow_trigger_views that are in the views array but are not in the currentTriggerViews array

    const triggerViewsToDelete = currentTriggerViews.filter(({ id }) => !views.find(({ id: viewId }) => viewId === id));
    const triggerViewsToCreate = views.filter(({ id }) => !currentTriggerViews.find(({ id: viewId }) => viewId === id));

    const deletePromises = triggerViewsToDelete.map(({ id }) => $apiClient.builder.workflowTriggerViews.delete(currentTrigger.id, id));
    const createPromises = triggerViewsToCreate.map(({ id }) => $apiClient.builder.workflowTriggerViews.create(currentTrigger.id, id));

    const failedRequestsReasons = [];

    const results = await Promise.allSettled([...deletePromises, ...createPromises]);

    results.forEach((result, index) => {
      const view = index < triggerViewsToDelete.length ? triggerViewsToDelete[index] : triggerViewsToCreate[index];

      if (result.status === 'fulfilled') {
        if (index < triggerViewsToDelete.length) {
          newTriggerViews = newTriggerViews.filter(({ id }) => id !== view.id);
        } else {
          newTriggerViews.push(view);
        }
      } else {
        failedRequestsReasons.push(result.reason);
      }
    });

    commit('updateTriggerViews', { workflowTriggerId: currentTrigger.id, views: newTriggerViews });
    dispatch('reloadCurrentWorkflow');

    if (failedRequestsReasons.length > 0) {
      throw failedRequestsReasons[0];
    }
  },
  async loadWorkflowStepsEntityDependencies({ getters, commit }) {
    const stepsMap = getters.workflowStepsMap;

    const dependencies = {};
    const { $errorRescue, $apiClient } = useNuxtApp();

    try {
      await Promise.all(
        Object.keys(stepsMap).map(stepId => $apiClient.builder.entityDependencies.listBySourceIdAndType(
          stepId,
          'WorkflowStep',
        ).then((response) => {
          dependencies[stepId] = response;
        })),
      );
    } catch (error) {
      $errorRescue(this, error, 'loadWorkflowStepsEntityDependencies', { onlyReturnErrorMsg: true });
    }

    commit('setWorkflowStepsEntityDependencies', dependencies);
  },
};

export const namespaced = true;
